import { flow } from 'lodash';
import { call, put, select } from 'redux-saga/effects';

import { MARCAPS_SUPPORT } from 'const';
import * as AppActions from 'containers/App/actions';
import { getArtboardsJson } from 'containers/Artboards/services/getArtboardsJson';
import * as projectActions from 'containers/Project/actions';
import * as ProjectSelectors from 'containers/Project/selectors';
import { getScreensWithThumbnails } from 'containers/Project/services/getScreensWithThumbnails';
import { getThumbnailsInfo } from 'containers/Project/services/getThumbnailsInfo';
import { updateSurfacesThumbnailUrls } from 'containers/Project/services/updateSurfacesThumbnailUrls';
import { rootDocument as rootDocumentSelector } from 'containers/RootDocument/selectors';
import { orderedSurfaces } from 'containers/Surfaces/selectors';
import { Screens } from 'models';
import { BinderApiResponse, createBinder, getThumbnails } from 'services/api';
import { getUsedFontFaces } from 'services/getUsedFontFaces';
import { Notifications } from 'services/Notifications';
import { getPackageName } from 'utils/getPackageName';
import { intlGet } from 'utils/intlGet';
import { UploadProjectFlowArg } from '../models';

export function* createProjectBinder(arg: UploadProjectFlowArg): Generator<unknown, UploadProjectFlowArg> {
  const { rootDocumentId, binderForm } = arg;
  if (!binderForm || !binderForm.isValid) {
    return arg;
  }

  // Store the dismiss function to close the loading toast when creation is complete
  let dismissLoadingToast: () => void = () => {};

  try {
    yield put(projectActions.startBinderUpload());
    yield put(AppActions.unlockProjectContent());

    dismissLoadingToast = Notifications.loadingV2({
      title: intlGet('Notification.Loading', 'BinderUploadingTitle'),
      message: intlGet('Notification.Loading', 'BinderUploadingText'),
    });
    const artboardsJson = yield call(getArtboardsJson);
    const projectType = yield select(ProjectSelectors.projectType);
    const helperFileName = yield select(ProjectSelectors.helperFileName);
    const rootDocument = yield select(rootDocumentSelector);
    const archiveName = yield call(getPackageName, rootDocument, projectType);
    const fonts = yield call(getUsedFontFaces);

    // throw new Error('test error to test toasts');
    const crmProductIds = binderForm.crmProducts?.map(product => product.value) || [];
    const crmDetailGroupId = binderForm.crmDetailGroup?.value || '';
    const businessUnitNames = binderForm.businessUnits?.map(unit => unit.value) || [];
    const usedFontFaces: ReturnTypeSaga<typeof getUsedFontFaces> = yield call(getUsedFontFaces);
    const surfaces: Screens = (yield select(orderedSurfaces)).toJS();

    const { data: thumbnailsUrls }: ReturnTypeSaga<typeof getThumbnails> = yield call(
      getThumbnails,
      artboardsJson,
      helperFileName,
      usedFontFaces,
    );

    const updatedSurfaces = flow(
      getScreensWithThumbnails,
      screens => updateSurfacesThumbnailUrls(screens, thumbnailsUrls),
    )(surfaces);

    const thumbnailsInfo: ReturnTypeSaga<typeof getThumbnailsInfo> = yield call(getThumbnailsInfo, updatedSurfaces);
    const projectProps = {
      artboardsJson,
      archiveName,
      fonts,
      projectType,
      helperFileName,
      thumbnailsInfo,
    };

    try {
      const response: BinderApiResponse = yield call(
        createBinder,
        rootDocumentId,
        {
          binderProps: {
            name: binderForm.binderName,
            crmProduct: crmProductIds,
            crmDetailGroup: crmDetailGroupId,
            businessUnits: businessUnitNames,
          },
          projectProps: { ...projectProps },
        },
      );
      if (response) {
        const { binderLink } = response;

        dismissLoadingToast();

        Notifications.successV2({
          title: intlGet('Notification', 'Success'),
          message: intlGet('Notification.Success', 'BinderCreated'),
          link: binderLink,
          linkText: intlGet('Notification.Success', 'BinderLink'),
        });
      }

    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      throw new Error(error?.message ?? String(error));
    }

  } catch (error) {
    if (dismissLoadingToast) {dismissLoadingToast();}

    Notifications.errorV2({
      title: intlGet('Notification', 'Error'),
      message:  intlGet('Notification.Error', 'BinderNotCreated'),
      link: MARCAPS_SUPPORT,
      linkText: ' contact support.',
    });
    throw new Error(error?.message ?? String(error));
  } finally {
    yield put(projectActions.endBinderUpload());
  }

  return arg;
}
